// NotificationBell.tsx — منقول من frontend/src/components/Header.js (جرس التنبيهات). يظهر لمن
// يملك صلاحيّة "notifications"، يجلب العدد عند التحميل والقائمة عند الفتح.
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IconBell } from '@tabler/icons-react';
import api from '../lib/api';
import { hasActionPermission } from '../lib/accessControl';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Separator } from '../components/ui/separator';

interface NotificationItem {
	title?: string;
	detail?: string;
	link?: string;
	severity?: 'error' | 'warning' | string;
}

export function NotificationBell() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const canSeeNotifs = hasActionPermission('notifications');
	const [items, setItems] = useState<NotificationItem[]>([]);
	const [count, setCount] = useState(0);

	useEffect(() => {
		if (!canSeeNotifs) return;
		let alive = true;
		api
			.get('/notifications/count')
			.then((r) => {
				if (alive) setCount(r.data?.count || 0);
			})
			.catch(() => undefined);
		return () => {
			alive = false;
		};
	}, [canSeeNotifs]);

	const loadItems = async () => {
		try {
			const r = await api.get('/notifications');
			setItems(r.data?.items || []);
			setCount(r.data?.total || 0);
		} catch {
			/* تجاهل */
		}
	};

	if (!canSeeNotifs) return null;

	return (
		<DropdownMenu onOpenChange={(open) => open && loadItems()}>
			<DropdownMenuTrigger asChild>
				<Button variant="ghost" size="icon" className="relative" aria-label="notifications">
					<IconBell className="size-5" />
					{count > 0 && (
						<Badge variant="destructive" className="absolute -end-1 -top-1 h-4 min-w-4 justify-center rounded-full px-1 text-[10px]">
							{count > 99 ? '99+' : count}
						</Badge>
					)}
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="end" className="w-80 max-h-96 overflow-y-auto p-0">
				<div className="px-3 py-2 font-bold">
					{t('notifications', 'التنبيهات')} ({count})
				</div>
				<Separator />
				{items.length === 0 ? (
					<p className="p-3 text-sm text-muted-foreground">{t('no_notifications', 'لا تنبيهات')}</p>
				) : (
					items.slice(0, 15).map((n, i) => (
						<DropdownMenuItem
							key={i}
							className="flex-col items-start gap-0.5 whitespace-normal border-s-2 py-2"
							style={{ borderInlineStartColor: n.severity === 'error' ? 'var(--destructive)' : n.severity === 'warning' ? 'var(--smart-brandGold)' : 'var(--primary)' }}
							onClick={() => n.link && navigate(n.link)}
						>
							<p className="text-sm font-bold">{n.title}</p>
							{n.detail && <p className="text-xs text-muted-foreground">{n.detail}</p>}
						</DropdownMenuItem>
					))
				)}
				<Separator />
				<DropdownMenuItem className="justify-center font-bold" onClick={() => navigate('/notifications')}>
					{t('view_all', 'عرض الكلّ')}
				</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	);
}
